import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

interface NavigationState {
  lastVisitedPath: string | null;
  lastVisitedParams: Record<string, string> | null;
  lastVisitedTimestamp: number | null;
  previousPath: string | null;
  /**
   * The desktop week grid's open row — 0 breakfast, 1 lunch, 2 dinner. The
   * other two are drawn as thin strips. A device preference, not a route, so
   * clearNavigationState leaves it alone. Absent on a profile persisted before
   * it existed; read it through selectPlannerFocusRow.
   */
  plannerFocusRow?: number;
}

const initialState: NavigationState = {
  lastVisitedPath: null,
  lastVisitedParams: null,
  lastVisitedTimestamp: null,
  previousPath: null,
};

const ONE_HOUR_MS = 60 * 60 * 1000;

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    setLastVisitedRoute: (
      state,
      action: PayloadAction<{ path: string; params?: Record<string, string> }>
    ) => {
      // The same route reports itself more than once — params arrive in a pass
      // after the pathname does. previousPath means the last *different* path;
      // letting a re-report overwrite it with the current one breaks
      // recipe-detail's back button, which navigates to it.
      if (action.payload.path !== state.lastVisitedPath) {
        state.previousPath = state.lastVisitedPath;
      }
      state.lastVisitedPath = action.payload.path;
      state.lastVisitedParams = action.payload.params || null;
      state.lastVisitedTimestamp = Date.now();
    },
    setPlannerFocusRow: (state, action: PayloadAction<number>) => {
      state.plannerFocusRow = action.payload;
    },
    clearNavigationState: (state) => {
      state.lastVisitedPath = null;
      state.lastVisitedParams = null;
      state.lastVisitedTimestamp = null;
      state.previousPath = null;
    },
  },
});

export const { setLastVisitedRoute, setPlannerFocusRow, clearNavigationState } = navigationSlice.actions;
export default navigationSlice.reducer;

// Selectors
export const selectLastVisitedPath = (state: RootState) => state.navigation?.lastVisitedPath ?? null;
export const selectLastVisitedParams = (state: RootState) => state.navigation?.lastVisitedParams ?? null;
export const selectLastVisitedTimestamp = (state: RootState) => state.navigation?.lastVisitedTimestamp ?? null;

export const selectPlannerFocusRow = (state: RootState): number | undefined => state.navigation?.plannerFocusRow;

export const selectPreviousPath = (state: RootState) => state.navigation?.previousPath ?? null;

export const selectShouldRestoreNavigation = (state: RootState): boolean => {
  const timestamp = state.navigation?.lastVisitedTimestamp;
  if (!timestamp) return false;
  return Date.now() - timestamp < ONE_HOUR_MS;
};
